/**
 * Auth session for the CRM.
 *
 * Flow: Firebase Auth signs the user in, then we check the email against the
 * `skidaCrmUsers` allowlist. A signed-in-but-not-approved user is signed
 * straight back out and shown the rejection banner — no app session is created,
 * matching the design's error state.
 *
 * ## Two ways in, and only one of them reaches the product project
 *
 * Google is the original and the better one: its popup yields an OAuth
 * credential that `signInToProduct` replays against the consumer project, so
 * product-backed columns work without a second sign-in.
 *
 * Email and password — enabled in Firebase Auth with `passwordRequired: true`,
 * checked against the project's own config rather than assumed — has no such
 * credential. A password is proof to *this* project and cannot be replayed to
 * another one, so an email login reaches the CRM's own data and nothing else
 * until the person presses Connect product data, which runs the Google popup
 * for the product app alone.
 *
 * That is a real limitation rather than an oversight, and the screen says so.
 * The alternative — asking for the same password twice against two projects —
 * would mean two password databases to keep in step, which is how one of them
 * ends up wrong.
 *
 * ## Creating an account is ordinary, because the allowlist is the gate
 *
 * Email and password with no way to make one is a door with no handle: an
 * address added to `skidaCrmUsers` had no Firebase Auth account behind it,
 * nothing to sign in with and no password to reset. Google sign-in worked
 * precisely because Google makes the account on the way past.
 *
 * So account creation is the plain one: address, password, verification email.
 * It grants nothing by itself. `firestore.rules` admits a login that is on the
 * allowlist, verified, and not disabled — a stranger who signs up gets an
 * account that can read nothing, and is told so by the rejection banner.
 *
 * This is also not a new exposure. Email/password is enabled on the project, so
 * the sign-up endpoint has always been reachable by anybody who cared to call
 * it directly; the button only stops pretending otherwise. See the note at the
 * top of `storage.rules`, which has said the same since August.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from 'firebase/auth'
import {
  auth,
  connectProduct,
  onProductAuthChanged,
  signInToProduct,
  signOutEverywhere,
} from '@/shared/firebase/client'
import { fetchCrmUser, saveOwnProfile } from './repository/crmUserRepository'
import { hasStaffRole, type CrmRole, type CrmUser } from '@/types/crm'

/**
 * `unverified` is its own state, and it has to be.
 *
 * Firestore refuses every read to a login whose address is unverified —
 * `isCrmUser()` in the rules, and `fetchCrmUser` checks the same thing before
 * asking. An email and password account is unverified the day an administrator
 * creates it, so without this state the first thing a new colleague sees is
 * "not an approved email address": they ring the administrator, who looks at
 * the allowlist, finds them on it, and neither of them is any the wiser.
 *
 * The session is kept rather than signed out, so the resend button has a user
 * to send on behalf of. Nothing is readable in this state anyway.
 */
export type AuthStatus = 'loading' | 'signed-out' | 'authorized' | 'rejected' | 'unverified'

interface AuthContextValue {
  status: AuthStatus
  user: CrmUser | null
  /** Email of the account that was rejected, for the error banner. */
  rejectedEmail: string | null
  /** Signed in, on the list or not, but the address has never been verified. */
  unverifiedEmail: string | null
  /** Send the verification link again, to the account currently signed in. */
  resendVerification: () => Promise<void>
  /** Non-auth errors (popup blocked, network) worth surfacing. */
  error: string | null
  signInWithGoogle: () => Promise<void>
  /** Email and password, for the accounts that have no Google identity. */
  signInWithEmail: (email: string, password: string) => Promise<void>
  /** Make one. Sends the verification the rules require before anything opens. */
  createAccount: (email: string, password: string) => Promise<void>
  /** Send a reset link. Never reports whether the address had an account. */
  sendPasswordReset: (email: string) => Promise<void>
  logOut: () => Promise<void>
  isAdmin: boolean
  /** Every role this login holds — see `CrmUser.roles`. Empty when signed out. */
  roles: CrmRole[]
  /** `admin` or `sales`. The three outside roles are not staff. */
  isStaff: boolean
  /**
   * Whether the product project's session exists. Product-backed columns are
   * blank without it; everything the CRM owns works either way.
   */
  productConnected: boolean
  /** The Google account the product session belongs to, when there is one. */
  productEmail: string | null
  /** Must be called from a click — see `connectProduct`. */
  connectProductData: (chooseAccount?: boolean) => Promise<void>
  productError: string | null
  /**
   * Your own name, title, phone and employer. Updates the session in place —
   * no re-sign-in. Phone and company exist for the digital business card.
   */
  saveProfile: (
    displayName: string,
    title: string,
    phone: string,
    company: string,
    photoUrl: string,
  ) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<CrmUser | null>(null)
  const [rejectedEmail, setRejectedEmail] = useState<string | null>(null)
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [productConnected, setProductConnected] = useState(false)
  const [productError, setProductError] = useState<string | null>(null)
  const [productEmail, setProductEmail] = useState<string | null>(null)

  // Tracked separately from the CRM session, because the two can genuinely
  // differ: a restored page load brings back whichever sessions were persisted,
  // and the product one can be absent while the CRM one is fine.
  useEffect(
    () =>
      onProductAuthChanged((signedIn, email) => {
        setProductConnected(signedIn)
        setProductEmail(email)
      }),
    [],
  )

  /**
   * Stop waiting eventually.
   *
   * `onAuthStateChanged` normally fires within a few hundred milliseconds, with
   * null when nobody is signed in. When it does not — Firebase Auth persists
   * through IndexedDB, and a browser that refuses it can leave the listener
   * never firing at all — the screen sits on "Signing you in…" with no way out.
   * Found by loading the published app in a headless browser, where exactly
   * that happens.
   *
   * A signed-out visitor seeing the sign-in screen a beat late is a much better
   * failure than one watching a spinner forever, so after eight seconds we
   * assume nobody is signed in. A real session that arrives afterwards still
   * replaces this — the listener below is not cancelled.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setStatus((prev) => (prev === 'loading' ? 'signed-out' : prev))
    }, 8000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    return onAuthStateChanged(auth, async (fbUser: User | null) => {
      if (!fbUser) {
        setUser(null)
        setStatus((prev) => (prev === 'rejected' ? 'rejected' : 'signed-out'))
        return
      }

      const email = fbUser.email ?? ''

      // Before the allowlist, because the allowlist cannot be read from here
      // without it and the two failures need different words on the screen.
      if (!fbUser.emailVerified) {
        setUnverifiedEmail(email)
        setRejectedEmail(null)
        setUser(null)
        setStatus('unverified')
        return
      }
      setUnverifiedEmail(null)

      try {
        const crmUser = await fetchCrmUser(email, fbUser.emailVerified)
        if (!crmUser) {
          setRejectedEmail(email)
          setUser(null)
          setStatus('rejected')
          await signOutEverywhere()
          return
        }
        setRejectedEmail(null)
        setUser({ ...crmUser, displayName: crmUser.displayName || fbUser.displayName || email })
        setStatus('authorized')
      } catch (err) {
        console.warn('[crm] allowlist check failed', err)
        setUser(null)
        const code = (err as { code?: string }).code ?? ''
        if (code === 'permission-denied') {
          // A rules rejection on the allowlist read means the same thing as a
          // missing document: not approved.
          setRejectedEmail(email)
          setStatus('rejected')
        } else {
          // Anything else — offline, a blocked request, a timeout — is not an
          // answer about the address, and saying "not approved" sent a listed
          // member of staff to the administrator for nothing (28 Oct 2026).
          setRejectedEmail(null)
          setError(
            `Could not check your access just now (${code || (err instanceof Error ? err.message : 'unknown error')}). ` +
              'This is not about your address. Check your connection, turn off any blocker for this site, or try another browser, then sign in again.',
          )
          setStatus('signed-out')
        }
        await signOutEverywhere()
      }
    })
  }, [])

  const runSignIn = useCallback(async (provider: GoogleAuthProvider) => {
    setError(null)
    setRejectedEmail(null)
    setStatus('loading')
    try {
      const result = await signInWithPopup(auth, provider)
      // Replay the same credential against the product project, so product
      // reads work as this person rather than as nobody. Not awaited for
      // correctness — onAuthStateChanged drives the UI — but awaited so the
      // first product read does not race the sign-in.
      await signInToProduct(GoogleAuthProvider.credentialFromResult(result))
      // onAuthStateChanged takes it from here.
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        setStatus('signed-out')
        return
      }
      setError(
        code === 'auth/popup-blocked'
          ? 'Your browser blocked the sign-in popup. Allow popups for this site and try again.'
          : 'Sign-in failed. Try again, or contact an administrator if it keeps happening.',
      )
      setStatus('signed-out')
    }
  }, [])

  const signInWithGoogle = useCallback(() => {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    return runSignIn(provider)
  }, [runSignIn])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setError(null)
    setRejectedEmail(null)
    setStatus('loading')
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password)
      // No call to `signInToProduct`: a password is proof to this project and
      // cannot be replayed to the product one. Product-backed columns stay
      // blank until Connect product data is pressed.
      // onAuthStateChanged takes it from here.
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      setError(
        /**
         * One message for every wrong-credential code, on purpose.
         *
         * Firebase returns `auth/user-not-found` and `auth/wrong-password`
         * separately — and newer SDKs collapse both into
         * `auth/invalid-credential` for exactly this reason. Telling somebody
         * which half they got wrong tells anybody which addresses have accounts
         * here, and this is the sign-in screen of the company's own register.
         */
        code === 'auth/invalid-credential' ||
          code === 'auth/user-not-found' ||
          code === 'auth/wrong-password' ||
          code === 'auth/invalid-login-credentials'
          ? 'That email address and password do not match an account.'
          : code === 'auth/invalid-email'
            ? 'That does not look like an email address.'
            : code === 'auth/user-disabled'
              ? 'That account has been disabled. Contact an administrator.'
              : code === 'auth/too-many-requests'
                ? 'Too many attempts. Wait a few minutes and try again.'
                : 'Sign-in failed. Try again, or contact an administrator if it keeps happening.',
      )
      setStatus('signed-out')
    }
  }, [])

  const resendVerification = useCallback(async () => {
    const current = auth.currentUser
    if (!current) {
      setError('Sign in again, then ask for a new verification link.')
      return
    }
    setError(null)
    try {
      await sendEmailVerification(current)
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      setError(
        code === 'auth/too-many-requests'
          ? 'A link has already gone out. Check your inbox, including spam, before asking again.'
          : 'The verification link could not be sent. Try again shortly.',
      )
      throw err
    }
  }, [])

  const createAccount = useCallback(async (email: string, password: string) => {
    setError(null)
    setRejectedEmail(null)
    setStatus('loading')
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password)
      // Straight away, because nothing is readable until it is done — the
      // `unverified` screen the listener is about to show says so and offers to
      // send it again.
      await sendEmailVerification(cred.user)
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      setError(
        /**
         * The commonest case here is not a forgotten password — it is Google.
         *
         * An address that has only ever signed in with Google has an account
         * and no password on it, so "reset the password" sends nothing and
         * looks broken. The message names the likely cause instead.
         */
        code === 'auth/email-already-in-use'
          ? 'That address already has an account. If you set it up with Google, keep using Continue with Google — there is no password on it to sign in with.'
          : code === 'auth/weak-password'
            ? 'Pick a password of at least six characters.'
            : code === 'auth/invalid-email'
              ? 'That does not look like an email address.'
              : code === 'auth/too-many-requests'
                ? 'Too many attempts. Wait a few minutes and try again.'
                : 'The account could not be created. Try again shortly.',
      )
      setStatus('signed-out')
    }
  }, [])

  /**
   * Resetting a password, and what this project's settings make of it.
   *
   * `enableImprovedEmailPrivacy` is on, which means Firebase **sends nothing and
   * reports success** when the address has no account or has no password on it —
   * the second case being every account created by signing in with Google. So
   * this cannot tell a caller whether a message went out, and the screen must
   * not claim one did. See the copy at the call site.
   */
  const sendPasswordReset = useCallback(async (email: string) => {
    const address = email.trim()
    if (!address) {
      setError('Enter your email address first, then ask for a reset link.')
      throw new Error('no address')
    }
    setError(null)
    try {
      await sendPasswordResetEmail(auth, address)
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      // `auth/user-not-found` is swallowed deliberately: reporting it would turn
      // this into a way of asking which addresses have accounts. The screen says
      // a link is on its way if the address is known, which is true either way.
      if (code === 'auth/user-not-found') return
      setError(
        code === 'auth/invalid-email'
          ? 'That does not look like an email address.'
          : code === 'auth/too-many-requests'
            ? 'Too many attempts. Wait a few minutes and try again.'
            : 'The reset link could not be sent. Try again shortly.',
      )
      throw err
    }
  }, [])

  const connectProductData = useCallback(async (chooseAccount = false) => {
    setProductError(null)
    try {
      await connectProduct(chooseAccount)
    } catch (err) {
      const code = (err as { code?: string }).code ?? ''
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
      setProductError(
        code === 'auth/popup-blocked'
          ? 'Your browser blocked the popup. Allow popups for this site and try again.'
          : 'Could not connect to the product project. Try again, or sign in again.',
      )
    }
  }, [])

  const logOut = useCallback(async () => {
    await signOutEverywhere()
    setUser(null)
    setRejectedEmail(null)
    setStatus('signed-out')
  }, [])

  const saveProfile = useCallback(
    async (
      displayName: string,
      title: string,
      phone: string,
      company: string,
      photoUrl: string,
    ) => {
      if (!user) return
      await saveOwnProfile(user.email, displayName, title, phone, company, photoUrl)
      // The session carries the name every email template signs with — and now
      // the fields the business card is built from, which is the more visible
      // of the two: the card is regenerated from this object, QR included, so a
      // stale session would hand out a stale phone number.
      setUser({
        ...user,
        displayName: displayName.trim(),
        title: title.trim(),
        phone: phone.trim(),
        company: company.trim(),
        photoUrl: photoUrl.trim(),
      })
    },
    [user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      rejectedEmail,
      unverifiedEmail,
      resendVerification,
      error,
      signInWithGoogle,
      signInWithEmail,
      createAccount,
      sendPasswordReset,
      logOut,
      isAdmin: user?.roles.includes('admin') ?? false,
      /**
       * The role, and whether it is a staff one — 29 Aug 2026.
       *
       * `isStaff` is what every existing screen should ask before rendering: a
       * shareholder or an ambassador signing in must not be shown the CRM at
       * all, and finding out by watching each read fail is not a design.
       */
      roles: user?.roles ?? [],
      isStaff: user ? hasStaffRole(user.roles) : false,
      productConnected,
      productEmail,
      connectProductData,
      productError,
      saveProfile,
    }),
    [
      status, user, rejectedEmail, unverifiedEmail, resendVerification, error,
      signInWithGoogle, signInWithEmail, createAccount, sendPasswordReset, logOut,
      productConnected, productEmail, connectProductData, productError, saveProfile,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
